/**
 * Group Therapy screening QuestionnaireResponse helpers.
 * Outcome codes live on the Group screening note QR per PRD_GROUP_THERAPY.md.
 */

import type { MedplumClient } from '@medplum/core';
import type {
  Patient,
  Practitioner,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
  RelatedPerson,
  Device,
  Organization,
  PractitionerRole,
  ServiceRequest,
} from '@medplum/fhirtypes';
import {
  EPISODE_OF_CARE_CONTEXT_EXT,
  EOC_GROUP_EXT,
  EOC_GROUP_REFERENCE_EXT,
  GROUP_THERAPY_SCREENING_NOTE_LINK_IDS,
  GROUP_THERAPY_SCREENING_NOTE_QUESTIONNAIRE_URL,
  QUESTIONNAIRE_CANONICAL_URL_PREFIX,
  SCREENING_TYPE_CODES,
} from '@unify/fhir-model/constants';
import { careTeamAccountRefsFromEpisode } from '@unify/fhir-model/resources/episode-of-care';
import { transitionServiceRequestStage } from '@/lib/serviceRequestService';
import { getEpisodeOfCareGroupRef } from './groupEnrollment';
import { setRequestedGroupTemplateRef } from './requestedGroupTemplate';
import {
  GROUP_SCREENING_OUTCOME_CODES,
  type GroupScreeningOutcomeCode,
} from './types';

const OUTCOME_SYSTEM = 'https://unify.health/fhir/CodeSystem/group-screening-outcome';

type QuestionnaireResponseAuthor = Reference<
  Patient | Practitioner | RelatedPerson | Device | Organization | PractitionerRole
>;
type QuestionnaireResponseSource = Reference<
  Patient | Practitioner | RelatedPerson | PractitionerRole
>;

function isGroupScreeningOutcomeCode(v: string): v is GroupScreeningOutcomeCode {
  return (GROUP_SCREENING_OUTCOME_CODES as readonly string[]).includes(v);
}

function findItemByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId === linkId) return item;
    const nested = findItemByLinkId(item.item, linkId);
    if (nested) return nested;
  }
  return undefined;
}

/** Build QuestionnaireResponse.item[] for a Group screening note. */
export function buildGroupScreeningNoteItems(
  outcomeCode: GroupScreeningOutcomeCode,
  notes?: string,
): QuestionnaireResponseItem[] {
  const items: QuestionnaireResponseItem[] = [
    {
      linkId: GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.types,
      text: 'Screening types',
      answer: [
        {
          valueCoding: {
            code: SCREENING_TYPE_CODES.groupTherapy,
            display: 'Group Therapy',
          },
        },
      ],
    },
    {
      linkId: GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.groupOutcome,
      text: 'Group Therapy outcome',
      answer: [
        {
          valueCoding: {
            system: OUTCOME_SYSTEM,
            code: outcomeCode,
          },
        },
      ],
    },
  ];
  const trimmed = notes?.trim();
  if (trimmed) {
    items.push({
      linkId: GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.groupNotes,
      text: 'Group Therapy notes',
      answer: [{ valueString: trimmed }],
    });
    items.push({
      linkId: GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.overallOutcome,
      text: 'Overall outcome',
      answer: [{ valueString: trimmed }],
    });
  }
  return items;
}

/** Parse outcome code + notes from a Group screening QuestionnaireResponse. */
export function parseGroupScreeningNoteItems(qr: QuestionnaireResponse): {
  code: GroupScreeningOutcomeCode | undefined;
  comments: string | undefined;
} {
  const resultItem =
    findItemByLinkId(qr.item, GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.groupOutcome)
    ?? findItemByLinkId(qr.item, GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.result);
  const notesItem =
    findItemByLinkId(qr.item, GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.groupNotes)
    ?? findItemByLinkId(qr.item, GROUP_THERAPY_SCREENING_NOTE_LINK_IDS.notes);
  const codeRaw =
    resultItem?.answer?.[0]?.valueCoding?.code
    ?? resultItem?.answer?.[0]?.valueString
    ?? '';
  const code = isGroupScreeningOutcomeCode(codeRaw) ? codeRaw : undefined;
  const comments = notesItem?.answer?.[0]?.valueString?.trim() || undefined;
  return { code, comments };
}

export type SaveGroupScreeningInput = {
  patientId: string;
  serviceRequestId: string;
  episodeOfCareId?: string;
  outcome: GroupScreeningOutcomeCode;
  notes?: string;
  /** Destination Group Template HealthcareService/{id} (waitlist key). */
  destinationTemplateReference?: string;
  destinationTemplateDisplay?: string;
  /** Concrete destination cohort Group/{id} when transferring between groups. */
  destinationGroupId?: string;
  destinationGroupDisplay?: string;
  author?: QuestionnaireResponseAuthor;
};

/**
 * Persist a Group screening note as a QuestionnaireResponse and advance the
 * ServiceRequest when the outcome clears screening (or cancels for decline / not appropriate).
 */
export async function saveGroupScreeningQuestionnaireResponse(
  medplum: MedplumClient,
  input: SaveGroupScreeningInput,
): Promise<QuestionnaireResponse> {
  const existing = await medplum.searchResources('QuestionnaireResponse', {
    subject: `Patient/${input.patientId}`,
    questionnaire: GROUP_THERAPY_SCREENING_NOTE_QUESTIONNAIRE_URL,
    _sort: '-_lastUpdated',
    _count: '1',
  });
  let prior = existing[0];
  if (!prior) {
    const legacy = await medplum.searchResources('QuestionnaireResponse', {
      subject: `Patient/${input.patientId}`,
      questionnaire: `${QUESTIONNAIRE_CANONICAL_URL_PREFIX}group-therapy-screening-note`,
      _sort: '-_lastUpdated',
      _count: '1',
    });
    prior = legacy[0];
  }

  let accounts: Reference[] = [];
  let seatedOnCohort = false;
  if (input.episodeOfCareId) {
    try {
      const eoc = await medplum.readResource('EpisodeOfCare', input.episodeOfCareId);
      accounts = careTeamAccountRefsFromEpisode(eoc);
      seatedOnCohort = !!getEpisodeOfCareGroupRef(eoc)?.reference;
    } catch {
      accounts = [];
    }
  }

  const source: QuestionnaireResponseSource | undefined =
    input.author?.reference?.startsWith('Practitioner/')
    || input.author?.reference?.startsWith('Patient/')
    || input.author?.reference?.startsWith('RelatedPerson/')
    || input.author?.reference?.startsWith('PractitionerRole/')
      ? (input.author as QuestionnaireResponseSource)
      : undefined;

  const draft: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    ...(prior?.id ? { id: prior.id } : {}),
    status: 'completed',
    questionnaire: GROUP_THERAPY_SCREENING_NOTE_QUESTIONNAIRE_URL,
    subject: { reference: `Patient/${input.patientId}` },
    authored: new Date().toISOString(),
    ...(input.author ? { author: input.author } : {}),
    ...(source ? { source } : {}),
    item: buildGroupScreeningNoteItems(input.outcome, input.notes),
    extension: input.episodeOfCareId
      ? [
          {
            url: EPISODE_OF_CARE_CONTEXT_EXT,
            valueReference: { reference: `EpisodeOfCare/${input.episodeOfCareId}` },
          },
        ]
      : undefined,
    meta: {
      ...(prior?.meta ?? {}),
      ...(accounts.length ? { accounts } : {}),
    },
  };

  const saved = prior?.id
    ? await medplum.updateResource(draft)
    : await medplum.createResource(draft);

  const sr = await medplum.readResource('ServiceRequest', input.serviceRequestId);
  let nextSr: ServiceRequest = { ...sr };

  // Stamp screening Practitioner on ServiceRequest.performer (PRD §7).
  const authorRef = input.author?.reference;
  if (authorRef?.startsWith('Practitioner/')) {
    const already =
      nextSr.performer?.some((p) => p.reference === authorRef) ?? false;
    if (!already) {
      nextSr = {
        ...nextSr,
        performer: [
          ...(nextSr.performer ?? []),
          {
            reference: authorRef,
            ...(input.author?.display ? { display: input.author.display } : {}),
          },
        ],
      };
      nextSr = await medplum.updateResource(nextSr);
    }
  }

  const destinationTemplate = input.destinationTemplateReference?.trim();
  if (input.outcome === 'appropriate-different-group' && destinationTemplate) {
    setRequestedGroupTemplateRef(nextSr, {
      reference: destinationTemplate,
      ...(input.destinationTemplateDisplay
        ? { display: input.destinationTemplateDisplay }
        : {}),
    });
    nextSr = await medplum.updateResource(nextSr);
  }

  if (
    input.outcome === 'appropriate-different-group'
    && input.destinationGroupId?.trim()
    && input.episodeOfCareId
  ) {
    const eoc = await medplum.readResource('EpisodeOfCare', input.episodeOfCareId);
    const groupReference = {
      reference: input.destinationGroupId.startsWith('Group/')
        ? input.destinationGroupId.trim()
        : `Group/${input.destinationGroupId.trim()}`,
      ...(input.destinationGroupDisplay
        ? { display: input.destinationGroupDisplay }
        : {}),
    };
    const withoutOld = (eoc.extension ?? []).filter(
      (e) => e.url !== EOC_GROUP_EXT && e.url !== EOC_GROUP_REFERENCE_EXT,
    );
    await medplum.updateResource({
      ...eoc,
      extension: [
        ...withoutOld,
        { url: EOC_GROUP_EXT, valueReference: groupReference },
      ],
    });
    medplum.invalidateSearches('EpisodeOfCare');
  }

  const clearsScreening =
    input.outcome === 'appropriate-selected-group'
    || input.outcome === 'appropriate-different-group';
  const cancels =
    input.outcome === 'not-appropriate' || input.outcome === 'client-declined';

  if (clearsScreening) {
    // A client already seated on the cohort was enrolled ahead of screening, so clearing it is the
    // fulfilment event — otherwise nothing would ever move them off `planned`.
    const nextStage =
      seatedOnCohort && input.outcome === 'appropriate-selected-group' ? 'fulfilled' : 'matching';
    await transitionServiceRequestStage({
      live: true,
      medplum,
      serviceRequest: nextSr,
      nextStage,
    });
  } else if (cancels) {
    await transitionServiceRequestStage({
      live: true,
      medplum,
      serviceRequest: nextSr,
      nextStage: 'cancelled',
    });
  }

  medplum.invalidateSearches('QuestionnaireResponse');
  medplum.invalidateSearches('ServiceRequest');
  return saved;
}
